<template>
  <div class="former-select">
    <div
      :class="contentClasses"
      tabindex="0"
      @click="focus(true)"
      @keydown="focus(true)"
      @keydown.enter.prevent="focus(true)"
    >
      <span :class="labelClasses">
        {{ label }}
      </span>

      <font-awesome-icon
        class="former-select-chevron"
        icon="chevron-down"
      />
    </div>

    <former-select-popup
      v-if="visible"
      v-bind="popup"
      :desktop="desktop"
      @select="select"
      @close="focus(false)"
      @search="search"
    />
  </div>
</template>

<script>
  import FormerSelectPopup from '@/components/Former/Field/Select/Popup.vue';

  export default {
    name: 'FormerSelect',
    components: {
      FormerSelectPopup,
    },
    props: {
      desktop: {
        type: Boolean,
        default: false,
      },
      inline: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      field: {
        type: Object,
        default() {
          return {};
        },
      },
      paginate: {
        type: Object,
        default() {
          return {};
        },
      },
      value: {
        type: String,
        default: '',
      },
      color: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        visible: false,
      };
    },
    computed: {
      popup() {
        const {
          options,
          placeholder,
          processing,
        } = this.field;

        return {
          options,
          processing,
          title: placeholder,
        };
      },
      selected() {
        let found = null;

        this.field.options.some((option) => {
          if (option.value === this.value) {
            found = option;
          }

          return !!found;
        });

        return found;
      },
      label() {
        if (this.selected) {
          return this.selected.label;
        }

        return this.field.placeholder;
      },
      labelClasses() {
        const classes = ['former-select-label'];

        if (!this.selected) {
          classes.push('former-select-placeholder');
        }

        return classes;
      },
      contentClasses() {
        const classes = ['former-select-content'];

        if (this.visible) {
          classes.push('former-select-content-open');
        }

        if (this.inline) {
          classes.push('former-select-content-inline');
        }

        if (this.color) {
          classes.push(`former-select-content-color-${this.color}`);
        }

        if (this.disabled) {
          classes.push('former-select-content-disabled');
        }

        if (this.field.errors && this.field.errors.length) {
          classes.push('former-select-content-error');
        }

        return classes;
      },
    },
    methods: {
      search(value, paginate) {
        this.$emit('search', value, paginate);
      },
      focus(bool) {
        const toggling = typeof bool === 'boolean' ? bool : !this.visible;

        if (toggling && (this.disabled || this.field.disabled || this.field.processing)) {
          return;
        }

        this.visible = toggling;
      },
      select(option) {
        this.$emit('input', option.value);
        this.$emit('change', option.value);

        this.focus(false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';
  @import '@/assets/stylesheets/proposals/status.scss';

  .former-select {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }

  .former-select-content {
    @include field-reset;
    @include former-field;

    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    @include former-field-desktop;
  }

  .former-select-content-inline {
    border-radius: $button * 0.5;
  }

  @each $status in $status-colors {
    $key: nth($status, 1);
    $value: nth($status, 2);

    .former-select-content-color-#{$key} {
      background-color: $value;
      color: var(--cordel-color-constant-light);
    }
  }

  .former-select-content-error {
    background-color: var(--cordel-color-alert-danger);
    color: var(--cordel-color-constant-light);
  }

  .former-select-content-disabled {
    cursor: not-allowed;

    &:focus {
      box-shadow: none;
    }
  }

  .former-select-label {
    flex: 1;
    margin-right: $gutter;
  }

  .former-select-placeholder {
    color: var(--cordel-color-neutral-primary);

    .former-select-content-error & {
      color: var(--cordel-color-constant-light);
    }
  }

  .former-select-chevron {
    margin-bottom: -0.125em;
  }
</style>
